/**
 * Payout history + Users tabs — both simple tables driven by admin data.
 *
 * Feb 2026: the "Simulate billing failure" column and STK / M-Pesa messaging
 * were removed with the closed-loop launch — institutional billing is now
 * annual bank transfer + admin verify, so there is no automated STK to fail.
 *
 * Feb 26 2026: added Super Admin user lifecycle actions:
 *   • View read-only snapshot (`UserSnapshotDialog`)
 *   • Pause / Unfreeze toggle
 *   • Soft-delete with confirmation
 * All actions hit `/admin/users/{id}/...` endpoints in `admin_lifecycle.py`
 * and rely on the api client's CSRF interceptor.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, Pause, Play, Trash2, Loader2, RotateCcw } from "lucide-react";
import UserSnapshotDialog from "./UserSnapshotDialog";
import UserAvatar from "@/components/UserAvatar";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function PayoutHistoryTab({ history }) {
    return (
        <div className="border border-border bg-card rounded overflow-hidden">
            <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-secondary/30">
                    <tr>
                        <th className="text-left px-3 py-2">Timestamp</th>
                        <th className="text-left px-3 py-2">Criterion</th>
                        <th className="text-right px-3 py-2">Gross</th>
                        <th className="text-right px-3 py-2">Fee</th>
                        <th className="text-right px-3 py-2">Net</th>
                        <th className="text-left px-3 py-2">Conversation ID</th>
                    </tr>
                </thead>
                <tbody>
                    {history.map((h) => (
                        <tr key={h.id} className="border-t border-border transition-colors duration-150 hover:bg-muted/40">
                            <td className="px-3 py-2 mono text-xs">{new Date(h.ts).toLocaleString("en-KE")}</td>
                            <td className="px-3 py-2 uppercase">{h.criterion}</td>
                            <td className="px-3 py-2 text-right mono">KES {h.gross_kes}</td>
                            <td className="px-3 py-2 text-right mono text-danger">KES {h.fee_kes}</td>
                            <td className="px-3 py-2 text-right mono text-success">KES {h.net_kes}</td>
                            <td className="px-3 py-2 text-xs mono">{h.conversation_id}</td>
                        </tr>
                    ))}
                    {history.length === 0 && (
                        <tr><td colSpan="6" className="p-8 text-center text-muted-foreground">No payouts yet.</td></tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

function StatusCell({ status }) {
    const s = (status || "active").toLowerCase();
    if (s === "frozen")  return <span className="text-danger font-semibold uppercase text-[11px]" data-testid="user-status">FROZEN</span>;
    if (s === "paused")  return <span className="text-amber-500 font-semibold uppercase text-[11px]" data-testid="user-status">PAUSED</span>;
    if (s === "deleted") return <span className="text-muted-foreground font-semibold uppercase text-[11px]" data-testid="user-status">DELETED</span>;
    return <span className="text-success font-semibold uppercase text-[11px]" data-testid="user-status">Active</span>;
}

function ActionButton({ testid, title, onClick, disabled, busy, icon: Icon, variant = "default" }) {
    const base = "inline-flex items-center gap-1 px-2 py-1 rounded border text-[11px] font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed";
    const styles = {
        default: "border-border hover:bg-secondary",
        primary: "border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground",
        warn:    "border-amber-500/40 text-amber-600 hover:bg-amber-500 hover:text-white",
        success: "border-success/40 text-success hover:bg-success hover:text-white",
        danger:  "border-danger/40 text-danger hover:bg-danger hover:text-white",
    }[variant];
    return (
        <button
            type="button"
            data-testid={testid}
            title={title}
            onClick={onClick}
            disabled={disabled || busy}
            className={`${base} ${styles}`}
        >
            {busy
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : <Icon className="w-3 h-3" />}
            <span className="hidden lg:inline">{title}</span>
        </button>
    );
}

function UserRow({ u, onReload }) {
    const [snapshotOpen, setSnapshotOpen] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [confirmRestore, setConfirmRestore] = useState(false);
    const [busy, setBusy] = useState(null); // "pause" | "unfreeze" | "delete" | "restore" | null

    const status = (u.account_status || "active").toLowerCase();
    const isDeleted = status === "deleted";
    const isAdmin   = u.role === "admin";
    const isPaused  = status === "paused";
    const isFrozen  = status === "frozen";

    async function doPause() {
        setBusy("pause");
        try {
            await api.post(`/admin/users/${u.id}/pause`);
            toast.success(`${u.email} paused`);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to pause");
        } finally { setBusy(null); }
    }

    async function doUnfreeze() {
        setBusy("unfreeze");
        try {
            await api.post(`/admin/users/${u.id}/unfreeze`);
            toast.success(`${u.email} restored`);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to restore");
        } finally { setBusy(null); }
    }

    async function doDelete() {
        setBusy("delete");
        try {
            const { data } = await api.delete(`/admin/users/${u.id}`);
            toast.success(`${data.email} soft-deleted (records retained)`);
            setConfirmDelete(false);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        } finally { setBusy(null); }
    }

    async function doRestoreDeleted() {
        setBusy("restore");
        try {
            const { data } = await api.post(`/admin/users/${u.id}/restore`);
            toast.success(`${data.email} restored — account active again`);
            setConfirmRestore(false);
            await onReload();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to restore deleted user");
        } finally { setBusy(null); }
    }

    return (
        <>
            <tr className="border-t border-border transition-colors duration-150 hover:bg-muted/40" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                        <UserAvatar user={u} size={28} testid={`user-row-avatar-${u.id}`} />
                        <span>{u.full_name}</span>
                    </div>
                </td>
                <td className="px-3 py-2 mono text-xs break-all">{u.original_email || u.email}</td>
                <td className="px-3 py-2 mono text-xs">{u.phone}</td>
                <td className="px-3 py-2 uppercase font-semibold text-primary">{u.tier}</td>
                <td className="px-3 py-2"><StatusCell status={u.account_status} /></td>
                <td className="px-3 py-2 text-xs mono">{u.referred_by?.slice(-6) || "—"}</td>
                <td className="px-3 py-2 text-right mono">{u.referral_credits || 0}</td>
                <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end flex-wrap">
                        <ActionButton
                            testid={`user-snapshot-${u.id}`}
                            title="View"
                            icon={Eye}
                            variant="primary"
                            onClick={() => setSnapshotOpen(true)}
                        />
                        {isDeleted ? (
                            <ActionButton
                                testid={`user-restore-deleted-${u.id}`}
                                title="Restore"
                                icon={RotateCcw}
                                variant="success"
                                busy={busy === "restore"}
                                onClick={() => setConfirmRestore(true)}
                            />
                        ) : (isPaused || isFrozen) ? (
                            <ActionButton
                                testid={`user-unfreeze-${u.id}`}
                                title="Restore"
                                icon={Play}
                                variant="success"
                                busy={busy === "unfreeze"}
                                onClick={doUnfreeze}
                            />
                        ) : (
                            <ActionButton
                                testid={`user-pause-${u.id}`}
                                title="Pause"
                                icon={Pause}
                                variant="warn"
                                busy={busy === "pause"}
                                disabled={isAdmin}
                                onClick={doPause}
                            />
                        )}
                        <ActionButton
                            testid={`user-delete-${u.id}`}
                            title="Delete"
                            icon={Trash2}
                            variant="danger"
                            busy={busy === "delete"}
                            disabled={isAdmin || isDeleted}
                            onClick={() => setConfirmDelete(true)}
                        />
                    </div>
                </td>
            </tr>

            {snapshotOpen && (
                <UserSnapshotDialog
                    userId={u.id}
                    open={snapshotOpen}
                    onClose={() => setSnapshotOpen(false)}
                />
            )}

            <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                <AlertDialogContent data-testid="user-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Soft-delete this user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong className="mono">{u.email}</strong> will be tombstoned
                            and blocked from logging in. Trades, journal entries, and
                            audit records are retained for CMA / DPA compliance. The user
                            can be brought back via the green <em>Restore</em> button that
                            replaces this action on deleted rows — but recovery codes,
                            copy-trade subscriptions and brute-force counters will not
                            be recreated on restore.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="user-delete-confirm-btn"
                            onClick={doDelete}
                            disabled={busy === "delete"}
                            className="bg-danger text-white hover:bg-danger/90"
                        >
                            {busy === "delete" ? "Deleting…" : "Soft-delete user"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
                <AlertDialogContent data-testid="user-restore-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Restore this deleted user?</AlertDialogTitle>
                        <AlertDialogDescription>
                            <strong className="mono">{u.original_email || u.email}</strong> will
                            be moved back to <em>active</em> status and the original email
                            address rewritten onto the record. Retained data (trades, journal,
                            positions, notifications, follows, referrals) reattaches
                            automatically. Note: recovery codes, copy-trade subscriptions and
                            brute-force counters were purged at delete-time and will <em>not</em>
                            be recreated — the user will need to re-enrol / re-subscribe.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="user-restore-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="user-restore-confirm-btn"
                            onClick={doRestoreDeleted}
                            disabled={busy === "restore"}
                            className="bg-success text-white hover:bg-success/90"
                        >
                            {busy === "restore" ? "Restoring…" : "Restore user"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}

export function UsersTab({ users, onReload }) {
    const { sorted, sortKey, direction, requestSort } = useSortableData(users, {
        accessors: {
            name: (u) => u.full_name || "",
            email: (u) => u.original_email || u.email || "",
            phone: (u) => u.phone || "",
            tier: (u) => u.tier || "",
            status: (u) => u.account_status || "",
            ref_by: (u) => u.referred_by || "",
            credits: (u) => Number(u.referral_credits || 0),
        },
    });
    return (
        <div className="border border-border bg-card rounded overflow-hidden max-h-[600px] overflow-y-auto" data-testid="admin-users-table">
            <table className="w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-secondary/30 sticky top-0 z-10">
                    <tr>
                        <SortableTh label="Name"   sortKey="name"    currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Email"  sortKey="email"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Phone"  sortKey="phone"   currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Tier"   sortKey="tier"    currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Status" sortKey="status"  currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Ref by" sortKey="ref_by"  currentKey={sortKey} direction={direction} onSort={requestSort} />
                        <SortableTh label="Credits" sortKey="credits" currentKey={sortKey} direction={direction} onSort={requestSort} align="right" />
                        <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((u) => (
                        <UserRow key={u.id} u={u} onReload={onReload} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
